/**
 * Navigation V2 - Mobile Slide Navigation
 * Implements multi-level slide navigation using menuObject data structure
 */

(function () {
  "use strict";

  // Initialize when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initNavigationV2);
  } else {
    initNavigationV2();
  }

  function initNavigationV2() {
    const navigation = document.querySelector('[data-section-type="navigation-v2"]');
    if (!navigation) return;

    const navigationOverlay = navigation.querySelector(".js-navigation-overlay");
    const headerNavigation = navigation.querySelector(".header-navigation-v2");

    const updateOverlayPosition = function () {
      if (!navigationOverlay) return;
      const referenceEl = headerNavigation || navigation;
      const rect = referenceEl.getBoundingClientRect();
      const topValue = Math.max(rect.bottom, 0);
      navigationOverlay.style.setProperty("--navigation-overlay-top", `${topValue}px`);
    };
    updateOverlayPosition();
    window.addEventListener("resize", updateOverlayPosition);
    window.addEventListener("scroll", updateOverlayPosition, { passive: true });
    navigation.__updateNavOverlayPosition = updateOverlayPosition;

    // Get menuObject from global scope
    const menuData = (typeof menuObject !== "undefined" && menuObject && menuObject.links) 
      ? menuObject.links 
      : [];

    if (menuData.length === 0) {
      console.warn("menuObject not found, navigation may not work correctly");
    }

    // Initialize mobile menu
    const resetMobileNavigation = initMobileMenu(navigation, menuData, updateOverlayPosition);

    // Initialize desktop hover
    initDesktopHover(navigation);

    if (navigationOverlay) {
      navigationOverlay.addEventListener("click", function () {
        const mobileMenuIcon = navigation.querySelector(".js-mobile-menu-icon-v2");

        if (headerNavigation && headerNavigation.classList.contains("header-navigation-v2--open")) {
          headerNavigation.classList.remove("header-navigation-v2--open");
          if (mobileMenuIcon) {
            mobileMenuIcon.setAttribute("aria-expanded", "false");
          }
          document.body.classList.remove("mobile-menu-open");
          if (resetMobileNavigation) {
            resetMobileNavigation();
          }
        }

        closeDesktopDropdowns(navigation);
        document.body.classList.remove("desktop-nav-open");
        requestAnimationFrame(updateOverlayPosition);
      });
    }

    // Expose toggle function for external use
    window.toggleMobileMenuV2 = function () {
      const nav = document.querySelector('[data-section-type="navigation-v2"]');
      if (!nav) return;

      const headerNav = nav.querySelector(".header-navigation-v2");
      const mobileMenuIcon = nav.querySelector(".js-mobile-menu-icon-v2");

      if (!headerNav) return;

      const isOpen = headerNav.classList.toggle("header-navigation-v2--open");
      if (mobileMenuIcon) {
        mobileMenuIcon.setAttribute("aria-expanded", isOpen);
      }
      document.body.classList.toggle("mobile-menu-open", isOpen);
      if (!isOpen) {
        document.body.classList.remove("desktop-nav-open");
      }
      if (navigationOverlay) {
        requestAnimationFrame(updateOverlayPosition);
      }

      // Reset navigation when closing
      if (!isOpen && resetMobileNavigation) {
        resetMobileNavigation();
      }
    };
  }

  /**
   * Find menu data by title and URL
   */
  function findMenuData(menuData, title, url) {
    if (!menuData || menuData.length === 0) return null;

    const normalizeUrl = function(u) {
      if (!u || u === "#") return "#";
      try {
        const urlObj = new URL(u, window.location.origin);
        return urlObj.pathname + urlObj.search;
      } catch(e) {
        return u;
      }
    };
    const normalizedUrl = normalizeUrl(url);

    // Search level 1
    for (let i = 0; i < menuData.length; i++) {
      const link = menuData[i];
      const linkUrl = normalizeUrl(link.url);

      if (link.title === title && (linkUrl === normalizedUrl || linkUrl === url || link.url === url)) {
        return { link: link, index: i, level: 1 };
      }

      // Search level 2
      if (link.links && link.links.length > 0) {
        for (let j = 0; j < link.links.length; j++) {
          const childLink = link.links[j];
          const childUrl = normalizeUrl(childLink.url);

          if (childLink.title === title && (childUrl === normalizedUrl || childUrl === url || childLink.url === url)) {
            return { 
              link: childLink, 
              parent: link, 
              index: j, 
              level: 2, 
              parentIndex: i 
            };
          }

          // Search level 3
          if (childLink.links && childLink.links.length > 0) {
            for (let k = 0; k < childLink.links.length; k++) {
              const grandChildLink = childLink.links[k];
              const grandChildUrl = normalizeUrl(grandChildLink.url);

              if (grandChildLink.title === title && (grandChildUrl === normalizedUrl || grandChildUrl === url || grandChildLink.url === url)) {
                return { 
                  link: grandChildLink, 
                  parent: childLink, 
                  grandParent: link, 
                  index: k, 
                  level: 3, 
                  parentIndex: j, 
                  grandParentIndex: i 
                };
              }
            }
          }
        }
      }
    }
    return null;
  }

  /**
   * Find menu DOM element by data attributes
   */
  function findMenuElement(menusContainer, menuData) {
    if (!menusContainer || !menuData) return null;

    const title = menuData.link.title;
    const url = menuData.link.url;

    // Normalize URL for comparison
    const normalizeUrl = function(u) {
      if (!u || u === "#") return "#";
      try {
        const urlObj = new URL(u, window.location.origin);
        return urlObj.pathname + urlObj.search;
      } catch(e) {
        return u;
      }
    };
    const normalizedUrl = normalizeUrl(url);

    // Try to find by data-parent-title (title match is most important)
    const menus = menusContainer.querySelectorAll(".mobile-site-nav-v2__menu");
    for (let i = 0; i < menus.length; i++) {
      const menu = menus[i];
      const menuTitle = menu.getAttribute("data-parent-title");
      const menuUrl = menu.getAttribute("data-parent-url");
      const normalizedMenuUrl = normalizeUrl(menuUrl);

      // Match by title first, then URL (both normalized)
      if (menuTitle === title && (normalizedMenuUrl === normalizedUrl || menuUrl === url || normalizedUrl === "#" || normalizedMenuUrl === "#")) {
        return menu;
      }
    }

    // Fallback: try data-parent-id pattern
    if (menuData.level === 2 && menuData.parentIndex !== undefined) {
      const parentId = `nav-${menuData.parentIndex + 1}`;
      const menu = menusContainer.querySelector(`.mobile-site-nav-v2__menu[data-parent-id="${parentId}"]`);
      if (menu) return menu;
    } else if (menuData.level === 3 && menuData.parentIndex !== undefined && menuData.grandParentIndex !== undefined) {
      const parentId = `nav-${menuData.grandParentIndex + 1}-${menuData.parentIndex + 1}`;
      const menu = menusContainer.querySelector(`.mobile-site-nav-v2__menu[data-parent-id="${parentId}"]`);
      if (menu) return menu;
    }

    return null;
  }

  /**
   * Mobile Menu Functionality
   */
  function initMobileMenu(navigation, menuData, overlayUpdater) {
    const mobileMenuIcon = document.querySelector(".js-mobile-menu-icon-v2") || 
                          navigation.querySelector(".js-mobile-menu-icon-v2");
    const headerNavigation = navigation.querySelector(".header-navigation-v2");
    const mobileNav = navigation.querySelector(".mobile-site-nav-v2");
    const menusContainer = navigation.querySelector(".mobile-site-nav-v2__menus-container");

    let activeMenuStack = [];
    let superParentData = null;

    // Open/close mobile menu
    if (mobileMenuIcon) {
      mobileMenuIcon.addEventListener("click", function () {
        const isOpen = headerNavigation.classList.toggle("header-navigation-v2--open");
        mobileMenuIcon.setAttribute("aria-expanded", isOpen);
        document.body.classList.toggle("mobile-menu-open", isOpen);
        if (!isOpen) {
          document.body.classList.remove("desktop-nav-open");
        }
        if (typeof overlayUpdater === "function") {
          requestAnimationFrame(overlayUpdater);
        }

        if (!isOpen) {
          resetMobileNavigation();
        }
      });
    }

    // Reset navigation to initial state
    function resetMobileNavigation() {
      activeMenuStack = [];
      superParentData = null;

      // Hide all menus
      const allMenus = navigation.querySelectorAll(".mobile-site-nav-v2__menu");
      allMenus.forEach(function (menu) {
        menu.classList.remove("mobile-site-nav-v2__menu--active", "mobile-site-nav-v2__menu--previous");
        menu.style.display = "none";
        menu.style.visibility = "hidden";
        
        // Remove parent headers
        const parentHeaders = menu.querySelectorAll(".mobile-site-nav-v2__parent-header");
        parentHeaders.forEach(function (header) {
          header.remove();
        });
      });

      // Reset all toggles
      const allToggles = navigation.querySelectorAll(".js-mobile-nav-toggle-v2");
      allToggles.forEach(function (toggle) {
        toggle.setAttribute("aria-expanded", "false");
        const item = toggle.closest(".mobile-site-nav-v2__item");
        if (item) {
          item.classList.remove("mobile-site-nav-v2__item--active-parent");
        }
      });

      // Show level 1 items
      if (mobileNav) {
        mobileNav.classList.remove("mobile-site-nav-v2--level-2", "mobile-site-nav-v2--level-3");
        const level1Items = mobileNav.querySelectorAll('li[data-level="1"]');
        level1Items.forEach(function (item) {
          item.style.display = "flex";
        });
      }
    }

    /**
     * Create parent header element
     */
    function createParentHeader(menuData, isSuperParent) {
      const header = document.createElement("li");
      header.className = isSuperParent
        ? "mobile-site-nav-v2__parent-header mobile-site-nav-v2__parent-header--super"
        : "mobile-site-nav-v2__parent-header";

      const linkEl = document.createElement("a");
      linkEl.href = menuData.link.url || "#";
      linkEl.className = "mobile-site-nav-v2__link mobile-site-nav-v2__parent-link";
      linkEl.textContent = menuData.link.title || "";

      const backButton = document.createElement("button");
      backButton.className = isSuperParent
        ? "btn--plain feather-icon mobile-site-nav-v2__icon mobile-site-nav-v2__back-icon js-mobile-nav-back-from-super"
        : "btn--plain feather-icon mobile-site-nav-v2__icon mobile-site-nav-v2__back-icon js-mobile-nav-back-from-menu";
      backButton.setAttribute("aria-label", "Back");
      backButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.5302 12.4694C10.6711 12.6103 10.7502 12.8014 10.7502 13.0006C10.7502 13.1999 10.6711 13.391 10.5302 13.5319C10.3893 13.6728 10.1982 13.7519 9.99891 13.7519C9.79965 13.7519 9.60856 13.6728 9.46766 13.5319L4.46766 8.53188C4.39774 8.4622 4.34226 8.3794 4.30441 8.28824C4.26656 8.19708 4.24707 8.09934 4.24707 8.00063C4.24707 7.90192 4.26656 7.80418 4.30441 7.71301C4.34226 7.62185 4.39774 7.53905 4.46766 7.46938L9.46766 2.46938C9.60856 2.32848 9.79965 2.24932 9.99891 2.24932C10.1982 2.24932 10.3893 2.32848 10.5302 2.46938C10.6711 2.61027 10.7502 2.80137 10.7502 3.00063C10.7502 3.19988 10.6711 3.39098 10.5302 3.53188L6.06204 8L10.5302 12.4694Z" fill="black"/></svg>';

      header.appendChild(linkEl);
      header.appendChild(backButton);
      return header;
    }

    /**
     * Slide to child menu
     */
    function slideToMenu(menu, clickedItem, clickedMenuData) {
      if (!menu || !menusContainer) return;

      // Get site header height
      const siteHeader = document.querySelector(".header-group");
      const siteHeaderHeight = siteHeader ? siteHeader.offsetHeight : 0;

      // Hide previous active menu
      const currentActiveMenu = navigation.querySelector(".mobile-site-nav-v2__menu--active");
      if (currentActiveMenu) {
        currentActiveMenu.classList.remove("mobile-site-nav-v2__menu--active");
        currentActiveMenu.style.display = "none";
        currentActiveMenu.style.visibility = "hidden";
      } else {
        // Hide level 1 items
        const level1Items = mobileNav.querySelectorAll('li[data-level="1"]');
        level1Items.forEach(function (item) {
          item.style.display = "none";
        });
      }

      // Determine target level (one deeper than clicked item)
      let targetLevel = 2;
      if (clickedMenuData && clickedMenuData.level) {
        targetLevel = clickedMenuData.level + 1;
      } else {
        // Fallback: get from menu's data-level or first child
        const menuLevel = parseInt(menu.getAttribute("data-level") || "2");
        const firstChildItem = menu.querySelector(".mobile-site-nav-v2__item:not(.mobile-site-nav-v2__parent-header)");
        targetLevel = firstChildItem
          ? parseInt(firstChildItem.getAttribute("data-level") || "2")
          : menuLevel;
      }

      // Determine super parent (level 1) for level 2 and 3 menus
      if (targetLevel === 2) {
        // For level 2, the clicked item is the super parent (level 1)
        if (clickedMenuData && clickedMenuData.level === 1) {
          superParentData = {
            link: clickedMenuData.link,
            index: clickedMenuData.index,
            level: 1
          };
        } else {
          // Fallback: use menu's data-parent-title
          const menuParentTitle = menu.getAttribute("data-parent-title");
          if (menuParentTitle && menuData && menuData.length > 0) {
            for (let i = 0; i < menuData.length; i++) {
              if (menuData[i].title === menuParentTitle) {
                superParentData = {
                  link: menuData[i],
                  index: i,
                  level: 1
                };
                break;
              }
            }
          }
        }
      } else if (targetLevel === 3) {
        // For level 3, get super parent from grandParent
        if (clickedMenuData && clickedMenuData.grandParent) {
          superParentData = {
            link: clickedMenuData.grandParent,
            index: clickedMenuData.grandParentIndex,
            level: 1
          };
        } else {
          // Fallback: extract from data-parent-id (nav-X-Y format)
          const menuParentId = menu.getAttribute("data-parent-id");
          if (menuParentId) {
            const navIdMatch = menuParentId.match(/^nav-(\d+)-(\d+)$/);
            if (navIdMatch && menuData && menuData.length > 0) {
              const grandParentIndex = parseInt(navIdMatch[1]) - 1;
              if (menuData[grandParentIndex]) {
                superParentData = {
                  link: menuData[grandParentIndex],
                  index: grandParentIndex,
                  level: 1
                };
              }
            }
          }
        }
      }

      // Create or update super parent header (level 1) for level 2 and 3 menus
      let superParentHeader = menu.querySelector(".mobile-site-nav-v2__parent-header--super");
      if (targetLevel === 3 && superParentHeader) {
        superParentHeader.remove();
        superParentHeader = null;
      }
      if (!superParentHeader && superParentData && targetLevel >= 2) {
        superParentHeader = createParentHeader(superParentData, true);
        menu.insertBefore(superParentHeader, menu.firstChild);
      }

      // Create immediate parent header (level 2) for level 3 menus only
      let parentHeader = menu.querySelector(".mobile-site-nav-v2__parent-header:not(.mobile-site-nav-v2__parent-header--super)");
      if (targetLevel === 3) {
        if (parentHeader) {
          parentHeader.remove();
          parentHeader = null;
        }

        let parentMenuData = null;
        // The clicked item (level 2) is the immediate parent
        if (clickedMenuData && clickedMenuData.level === 2 && clickedMenuData.link) {
          parentMenuData = {
            link: clickedMenuData.link,
            index: clickedMenuData.index,
            level: 2
          };
        } else {
          // Fallback: use menu's data-parent-title
          const menuParentTitle = menu.getAttribute("data-parent-title");
          const menuParentUrl = menu.getAttribute("data-parent-url");
          if (menuParentTitle) {
            parentMenuData = {
              link: {
                title: menuParentTitle,
                url: menuParentUrl || "#"
              },
              level: 2
            };
          }
        }

        if (parentMenuData) {
          parentHeader = createParentHeader(parentMenuData, false);
          // Insert before first menu item, after super parent header
          const firstMenuItem = menu.querySelector(".mobile-site-nav-v2__item:not(.mobile-site-nav-v2__parent-header)");
          if (firstMenuItem) {
            menu.insertBefore(parentHeader, firstMenuItem);
          } else {
            menu.appendChild(parentHeader);
          }
        }
      }

      // Position menu below site header
      menu.style.top = siteHeaderHeight + "px";
      menu.style.height = "calc(100vh - " + siteHeaderHeight + "px)";
      menu.style.left = "0";
      menu.style.right = "0";

      // Activate menu
      menu.classList.remove("mobile-site-nav-v2__menu--previous");
      menu.classList.add("mobile-site-nav-v2__menu--active");
      menu.style.display = "block";
      menu.style.visibility = "visible";

      // Make all items visible
      const menuItems = menu.querySelectorAll(".mobile-site-nav-v2__item");
      menuItems.forEach(function (item) {
        item.style.display = "flex";
      });

      const allParentHeaders = menu.querySelectorAll(".mobile-site-nav-v2__parent-header");
      allParentHeaders.forEach(function(header) {
        header.style.display = "flex";
      });

      // Update stack
      activeMenuStack.push({ 
        menu: menu, 
        menuData: clickedMenuData,
        clickedItem: clickedItem 
      });

      // Update nav level classes
      if (targetLevel === 2) {
        mobileNav.classList.add("mobile-site-nav-v2--level-2");
        mobileNav.classList.remove("mobile-site-nav-v2--level-3");
      } else if (targetLevel === 3) {
        mobileNav.classList.add("mobile-site-nav-v2--level-3");
      }

      // Update parent item state
      if (clickedItem) {
        clickedItem.classList.add("mobile-site-nav-v2__item--active-parent");
      }
    }

    /**
     * Slide back to parent menu
     */
    function slideBack() {
      if (activeMenuStack.length === 0) return;

      const currentStackItem = activeMenuStack.pop();
      const currentMenu = currentStackItem.menu;

      if (!currentMenu || !mobileNav) return;

      // Hide current menu
      currentMenu.classList.remove("mobile-site-nav-v2__menu--active");
      currentMenu.style.display = "none";
      currentMenu.style.visibility = "hidden";

      // Remove immediate parent header from current menu
      const immediateParentHeader = currentMenu.querySelector(
        ".mobile-site-nav-v2__parent-header:not(.mobile-site-nav-v2__parent-header--super)"
      );
      if (immediateParentHeader) {
        immediateParentHeader.remove();
      }

      // If going back to level 1, remove super parent header
      if (activeMenuStack.length === 0) {
        const superParentHeader = currentMenu.querySelector(".mobile-site-nav-v2__parent-header--super");
        if (superParentHeader) {
          superParentHeader.remove();
        }
        superParentData = null;
      }

      // Reset toggle state
      if (currentStackItem.clickedItem) {
        const toggle = currentStackItem.clickedItem.querySelector(".js-mobile-nav-toggle-v2");
        if (toggle) {
          toggle.setAttribute("aria-expanded", "false");
          currentStackItem.clickedItem.classList.remove("mobile-site-nav-v2__item--active-parent");
        }
      }

      // Show parent menu or level 1
      if (activeMenuStack.length > 0) {
        const parentStackItem = activeMenuStack[activeMenuStack.length - 1];
        const parentMenu = parentStackItem.menu;

        const siteHeader = document.querySelector(".header-group");
        const siteHeaderHeight = siteHeader ? siteHeader.offsetHeight : 0;
        parentMenu.style.top = siteHeaderHeight + "px";
        parentMenu.style.height = "calc(100vh - " + siteHeaderHeight + "px)";
        parentMenu.style.left = "0";
        parentMenu.style.right = "0";
        parentMenu.style.display = "block";
        parentMenu.style.visibility = "visible";
        parentMenu.classList.add("mobile-site-nav-v2__menu--active");

        const parentMenuItems = parentMenu.querySelectorAll(".mobile-site-nav-v2__item");
        parentMenuItems.forEach(function(item) {
          item.style.display = "flex";
        });

        const superParentHeader = parentMenu.querySelector(".mobile-site-nav-v2__parent-header--super");
        if (superParentHeader) {
          superParentHeader.style.display = "flex";
        }
      } else {
        // Going back to level 1
        const level1Items = mobileNav.querySelectorAll('li[data-level="1"]');
        level1Items.forEach(function (item) {
          item.style.display = "flex";
        });
        mobileNav.classList.remove("mobile-site-nav-v2--level-2", "mobile-site-nav-v2--level-3");

        // Reset all toggle states
        const allToggles = navigation.querySelectorAll(".js-mobile-nav-toggle-v2");
        allToggles.forEach(function(toggle) {
          toggle.setAttribute("aria-expanded", "false");
          const item = toggle.closest(".mobile-site-nav-v2__item");
          if (item) {
            item.classList.remove("mobile-site-nav-v2__item--active-parent");
          }
        });
      }
    }

    // Click to reveal submenu - event delegation
    if (navigation) {
      navigation.addEventListener("click", function (e) {
        // Check if click is on level 1 item (make entire item tappable)
        const clickedItem = e.target.closest(".mobile-site-nav-v2__item[data-level='1']");
        const toggle = e.target.closest(".js-mobile-nav-toggle-v2");

        let targetToggle = toggle;
        if (clickedItem && !toggle) {
          // Click was on level 1 item but not toggle - find toggle
          targetToggle = clickedItem.querySelector(".js-mobile-nav-toggle-v2");
          if (targetToggle) {
            e.preventDefault();
            e.stopPropagation();
          } else {
            return; // No toggle, allow normal navigation
          }
        } else if (targetToggle) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          return;
        }

        // Get clicked item data
        const item = targetToggle.closest(".mobile-site-nav-v2__item");
        if (!item) return;

        const link = item.querySelector(".mobile-site-nav-v2__link");
        if (!link) return;

        const linkText = link.textContent.trim();
        const linkHref = link.href || link.getAttribute("href") || "#";

        // Normalize URL
        const normalizeUrl = function(url) {
          if (!url || url === "#") return "#";
          try {
            const urlObj = new URL(url, window.location.origin);
            return urlObj.pathname + urlObj.search;
          } catch(e) {
            return url;
          }
        };
        const normalizedHref = normalizeUrl(linkHref);

        // Find menu data
        let clickedMenuData = null;
        if (menuData && menuData.length > 0) {
          clickedMenuData = findMenuData(menuData, linkText, linkHref);
          if (!clickedMenuData && normalizedHref !== linkHref) {
            clickedMenuData = findMenuData(menuData, linkText, normalizedHref);
          }
        }

        // Find menu element
        let menu = null;
        if (clickedMenuData) {
          menu = findMenuElement(menusContainer, clickedMenuData);
        }

        // Fallback: use data-nav-id
        if (!menu) {
          const navId = targetToggle.getAttribute("data-nav-id");
          
          if (navId && menusContainer) {
            // Try exact match first
            menu = menusContainer.querySelector(`.mobile-site-nav-v2__menu[data-parent-id="${navId}"]`);
            
            // If not found and navId is in format nav-X-Y, also try to find level 3 menu
            if (!menu && navId.match(/^nav-\d+-\d+$/)) {
              menu = menusContainer.querySelector(`.mobile-site-nav-v2__menu[data-parent-id="${navId}"]`);
            }
          }
        }
        
        // Additional fallback: if we have clickedMenuData but menu not found, try finding by title only
        if (!menu && clickedMenuData) {
          const menus = menusContainer.querySelectorAll(".mobile-site-nav-v2__menu");
          for (let i = 0; i < menus.length; i++) {
            const menuTitle = menus[i].getAttribute("data-parent-title");
            if (menuTitle === clickedMenuData.link.title) {
              menu = menus[i];
              break;
            }
          }
        }

        if (!menu) {
          console.warn("Menu not found for:", linkText);
          return;
        }

        // Create fallback clickedMenuData if needed
        if (!clickedMenuData && menu) {
          const menuParentTitle = menu.getAttribute("data-parent-title");
          const menuParentUrl = menu.getAttribute("data-parent-url");
          if (menuParentTitle) {
            // Check if this matches a level 1 item
            let actualLevel = 2;
            let parentIndex = -1;
            if (menuData && menuData.length > 0) {
              for (let i = 0; i < menuData.length; i++) {
                const normalizedMenuUrl = normalizeUrl(menuData[i].url);
                const normalizedParentUrl = normalizeUrl(menuParentUrl || "#");
                if (menuData[i].title === menuParentTitle && 
                    (normalizedMenuUrl === normalizedParentUrl || menuData[i].url === menuParentUrl)) {
                  actualLevel = 1;
                  parentIndex = i;
                  break;
                }
              }
            }
            clickedMenuData = {
              link: {
                title: menuParentTitle,
                url: menuParentUrl || "#"
              },
              level: actualLevel,
              index: parentIndex >= 0 ? parentIndex : undefined
            };
          }
        }

        // Check if menu is already active
        const isMenuActive = menu.classList.contains("mobile-site-nav-v2__menu--active") &&
                            menu.style.display !== "none" &&
                            menu.style.visibility !== "hidden";
        const isExpanded = targetToggle.getAttribute("aria-expanded") === "true";

        if (isExpanded && isMenuActive) {
          slideBack();
        } else {
          targetToggle.setAttribute("aria-expanded", "true");
          slideToMenu(menu, item, clickedMenuData);
        }
      });
    }

    // Handle back button clicks - event delegation
    if (navigation) {
      navigation.addEventListener("click", function (e) {
        // Super parent back button (goes to level 1)
        const superBackButton = e.target.closest(".js-mobile-nav-back-from-super");
        if (superBackButton) {
          e.preventDefault();
          e.stopPropagation();
          while (activeMenuStack.length > 0) {
            slideBack();
          }
          return;
        }

        // Normal back button (goes one level back)
        const backButton = e.target.closest(".js-mobile-nav-back-from-menu");
        if (backButton) {
          e.preventDefault();
          e.stopPropagation();
          slideBack();
          return;
        }

        // Parent link clicks
        const link = e.target.closest(".mobile-site-nav-v2__link");
        if (!link) return;

        const item = link.closest(".mobile-site-nav-v2__item");
        if (!item) return;

        const toggle = item.querySelector(".js-mobile-nav-toggle-v2");
        if (toggle && toggle.getAttribute("aria-expanded") === "true") {
          e.preventDefault();
          slideBack();
        }
      });
    }

    return resetMobileNavigation;
  }

  /**
   * Desktop Hover Functionality
   */
  function initDesktopHover(navigation) {
    const navItems = navigation.querySelectorAll(".site-nav-v2__item--has-dropdown");
    const body = document.body;

    function updateDesktopOverlayState() {
      const hasOpen = navigation.querySelector(".site-nav-v2__item--has-dropdown.open");
      if (hasOpen) {
        body.classList.add("desktop-nav-open");
      } else {
        body.classList.remove("desktop-nav-open");
      }
    }

    navItems.forEach(function (item) {
      const link = item.querySelector(".site-nav-v2__link");
      const dropdown = item.querySelector(".site-nav-v2__dropdown");

      if (!dropdown) return;

      item.addEventListener("mouseenter", function () {
        item.classList.add("open");
        updateDesktopOverlayState();
      });

      item.addEventListener("mouseleave", function () {
        item.classList.remove("open");
        updateDesktopOverlayState();
      });

      // Handle subnav item hover - make parent link blue
      const subnavLinks = dropdown.querySelectorAll(".site-nav-v2__dropdown-link");
      subnavLinks.forEach(function (subnavLink) {
        subnavLink.addEventListener("mouseenter", function () {
          const parentHeading = dropdown.querySelector(
            `.site-nav-v2__dropdown-heading[data-parent-heading="${this.getAttribute("data-parent-heading")}"]`
          );
          if (parentHeading) {
            parentHeading.style.color = "#1F47AA";
          }
          if (link) {
            link.style.color = "#1F47AA";
          }
        });

        subnavLink.addEventListener("mouseleave", function () {
          const parentHeading = dropdown.querySelector(
            `.site-nav-v2__dropdown-heading[data-parent-heading="${this.getAttribute("data-parent-heading")}"]`
          );
          if (parentHeading) {
            parentHeading.style.color = "";
          }
          if (link) {
            link.style.color = "";
          }
        });
      });
    });
  }

  function closeDesktopDropdowns(navigation) {
    const openItems = navigation.querySelectorAll(".site-nav-v2__item--has-dropdown.open");
    openItems.forEach(function (item) {
      item.classList.remove("open");
    });
  }

  // Close mobile menu when clicking outside
  document.addEventListener("click", function (e) {
    const navigation = document.querySelector('[data-section-type="navigation-v2"]');
    if (!navigation) return;

    const headerNavigation = navigation.querySelector(".header-navigation-v2");
    const mobileMenuIcon = document.querySelector(".js-mobile-menu-icon-v2") || 
                          navigation.querySelector(".js-mobile-menu-icon-v2");
    const overlayUpdater = navigation.__updateNavOverlayPosition;

    if (headerNavigation && headerNavigation.classList.contains("header-navigation-v2--open")) {
      if (!navigation.contains(e.target) && 
          !e.target.closest(".burger-icon-v2") && 
          !e.target.closest(".js-mobile-menu-icon-v2")) {
        headerNavigation.classList.remove("header-navigation-v2--open");
        if (mobileMenuIcon) {
          mobileMenuIcon.setAttribute("aria-expanded", "false");
        }
        document.body.classList.remove("mobile-menu-open");
        document.body.classList.remove("desktop-nav-open");
        if (typeof overlayUpdater === "function") {
          requestAnimationFrame(overlayUpdater);
        }
      }
    }

    const hasDesktopDropdown = navigation.querySelector(".site-nav-v2__item--has-dropdown.open");
    if (hasDesktopDropdown && !navigation.contains(e.target)) {
      closeDesktopDropdowns(navigation);
      document.body.classList.remove("desktop-nav-open");
      if (typeof overlayUpdater === "function") {
        requestAnimationFrame(overlayUpdater);
      }
    }
  });
})();

